import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid, makeStyles, Typography, Button, Tooltip } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import Viewer from '../Viewer';
import LoadingCard from '../UI/LoadingCard';
import AlertDialog from '../UI/AlertDialog';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import BundleDetails from './BundleDetails';
import DocumentDetails from './DocumentDetails';
import useWindowSize from '../../customHooks/useWindowSize';
import { CONSTANTS } from '../../constants/constants';
import { connect } from 'react-redux';
import FlowSteps from '../UI/FlowSteps';
import PageContentError, { Types } from '../UI/PageContentError';
import { Storage } from 'aws-amplify';
import SaveAltIcon from '@material-ui/icons/SaveAlt';
import IconButton from '@material-ui/core/IconButton';
import CSVExport from '../UI/CSVExport';
import DownloadFilesStep from '../UI/DownloadFilesStep';
import SignaturePositionsInfoAlert from './SignaturePositionInfoAlert';

const useStyles = makeStyles((theme) => ({
  middleContainer: {
    width: '100%',
    display: 'flex',
    flesDirection: 'row',
    justifyContent: 'space-between'
  },
  button: {
    margin: 5
  },
  mobileButton: {
    margin: 5,
    width: '48%',
    paddingTop: 10,
    paddingBottom: 10
  },
  stepperRoot: {
    backgroundColor: theme.palette.background.default
  },
  accordionRoot: {
    flex: 1,
    marginRight: 20,
    boxShadow: 'none',
    border: 'none',
    background: theme.palette.background.default
  },
  accordionDetail: {
    display: 'block'
  },
  itemText: {
    display: 'flex',
    flexDirection: 'row'
  },
  titleText: {
    fontWeight: 'bold'
  },
  buttonsBlock: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end'
  },
  content: {
    display: 'flex',
    flexGrow: 1,
    margin: '12px 0',
    '&$expanded': {
      margin: '12px 0'
    }
  },
  expanded: {},
  mobileGridItem: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 27,
    paddingRight: 15,
    paddingLeft: 15
  },
  topBtns: {
    paddingBottom: '1rem',
    textAlign: 'right',
    textTransform: 'uppercase'
  },
  downloadFilesStep: {
    paddingBottom: '1rem',
    textAlign: 'right',
    textTransform: 'uppercase',
    marginLeft: '1rem'
  }
}));

const downloadBundleDocumentsFromS3 = async (item, setPdfSignedUrl) => {
  let listNamesFilesS3 = [];
  let listFilesUrlS3 = [];
  let arrayPromise = item.document.items.map((document) => {
    let urlPdf = document.fileUrl.replace(/[.]\w{3,4}$/, '.pdf');
    listNamesFilesS3.push(document.name);
    return new Promise(async (resolve) => {
      const url = await Storage.get(urlPdf);
      resolve({ url, original: document.fileUrl });
    });
  });
  listFilesUrlS3 = await Promise.all(arrayPromise);
  setPdfSignedUrl(listFilesUrlS3);
};

const donwloadFiles = (files) => {
  for (var i = 0; i < files.length; i++) {
    setTimeout(window.open(files[i].url), 200 + i * 200);
  }
};

const MobileView = ({
  type,
  item,
  launch,
  match,
  appConfig,
  isLaunched
}) => {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation();
  const showButtonDownloadFilesStep = item.flow && type !== 'bundle';

  const { role } = appConfig;
  const hasPermission = [CONSTANTS.ROLES.ADMIN, CONSTANTS.ROLES.EDITOR].includes(role);
  const [pdfSignedUrl, setPdfSignedUrl] = useState(null);

  useEffect(() => {
    if (type === 'bundle') {
      downloadBundleDocumentsFromS3(item, setPdfSignedUrl);
    }
  }, []);

  return (
    <Grid container direction='row' alignItems='center'>
      <Grid item xs={12} className={classes.mobileGridItem} >
        <Button
          variant='outlined'
          size='large'
          className={classes.mobileButton}
          aria-label='delete'
          onClick={() => history.push(history.push(type === 'bundle' ? '/bundle' : '/document'))}
        >
          {t('processDetail:back')}
        </Button>
        {hasPermission && (type === 'bundle' && item.status === CONSTANTS.BUNDLE.STATUS.READY && !isLaunched) &&
          <Button
            variant='contained'
            color='primary'
            target='_blank'
            className={classes.mobileButton}
            onClick={launch}
          >
            {t('processDetail:startFlow')}
          </Button>
        }
        {hasPermission && (type === 'document' && item.status === CONSTANTS.DOCUMENT.STATUS.INIT && !item.bundleId) &&
          <Button
            variant='contained'
            size='large'
            color='primary'
            className={classes.mobileButton}
            component={RouterLink}
            to={`/document/flow/${item.id}`}
          >
            {t('processDetail:createFlow')}
          </Button>
        }
        <CSVExport data={[item]} type={item.type} />
        {showButtonDownloadFilesStep && <DownloadFilesStep flow={item.flow} />}
        {type === 'bundle' &&
          <Tooltip disableFocusListener title={t('processDetail:download')}>
            <IconButton
              className='document-downloadDocument'
              onClick={() => donwloadFiles(pdfSignedUrl)}
              style={{ color: 'black' }}>
              <SaveAltIcon style={{ fontSize: '18px' }} />
            </IconButton>
          </Tooltip>}

      </Grid>
      <Grid item xs={12} className={classes.mobileGridItem} >
        {item.flow && <FlowSteps document={item} nonLinear />}
      </Grid>
      <Grid item xs={12} className={classes.mobileGridItem} >
        <Accordion className={classes.accordionRoot} defaultExpanded>
          <AccordionSummary
            classes={{ content: classes.content, expanded: classes.expanded }}
            expandIcon={<ExpandMoreIcon />}
          >
            <Typography variant='h5'>{t(`processDetail:${type}`)}</Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accordionDetail}>
            {type === 'bundle' && <BundleDetails item={item} appConfig={appConfig} />}
            {type === 'document' && <DocumentDetails item={item} appConfig={appConfig} />}
          </AccordionDetails>
        </Accordion></Grid>
      {item &&
        <Grid item xs={12} >
          <Viewer match={match} type={type} document={type === 'bundle' ? item.document.items : item} bundleDocument={item} />
        </Grid>
      }
    </Grid>
  );
};

MobileView.propTypes = {
  type: PropTypes.string.isRequired,
  item: PropTypes.object.isRequired,
  launch: PropTypes.func,
  match: PropTypes.object,
  appConfig: PropTypes.object,
  isLaunched: PropTypes.bool
};

const DesktopView = ({
  type,
  item,
  launch,
  match,
  appConfig,
  isLaunched
}) => {
  const { t } = useTranslation();
  let history = useHistory();
  const classes = useStyles();

  const { role } = appConfig;
  const hasPermission = [CONSTANTS.ROLES.ADMIN, CONSTANTS.ROLES.EDITOR].includes(role);

  const mergeSteps = (doc) => {
    const { flow } = doc;
    const parsedFlow = typeof flow === 'string' ? JSON.parse(flow) : flow;
    if (parsedFlow && parsedFlow.steps.some(step => step.subFlowType === 'subflows')) {
      parsedFlow.steps = parsedFlow.steps.filter((step) => {
        return step.subFlowType !== 'bundle';
      });
      doc.flow = parsedFlow;
    }
    return doc;
  };

  const unifiedFlowSteps = item.bundle ? mergeSteps(item) : item;
  const showButtonDownloadFilesStep = item.flow && type !== 'bundle';

  return (
    <Grid container direction={'row'} justify={'center'} >
      <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center', paddingTop: 10 }}>
        {item.flow && <FlowSteps document={unifiedFlowSteps} nonLinear />}
      </Grid>
      <Grid item container xs={12} direction='row' justify='flex-end' >
        <Grid item xs={2} className={classes.topBtns}>
          <CSVExport data={[item]} type={item.type} />
        </Grid>
        <Grid item className={classes.downloadFilesStep}>
          {showButtonDownloadFilesStep && <DownloadFilesStep flow={item.flow} />}
        </Grid>
        <Grid item xs={1} />
      </Grid>
      <Grid item container xs={12} justify='center'>
        <Grid item xs={10} >
          <div className={classes.middleContainer}>
            <div className={classes.accordionRoot}>
              <div>
                <Typography variant='h6'>{t(`processDetail:${type}`)}</Typography>
              </div>
              <div className={classes.accordionDetail}>
                {type === 'bundle' && <BundleDetails item={item} appConfig={appConfig} />}
                {type === 'document' && <DocumentDetails item={item} appConfig={appConfig} />}
              </div>
            </div>
            <div className={classes.buttonsBlock}>
              {hasPermission && (type === 'bundle' && item.status === CONSTANTS.BUNDLE.STATUS.READY && !isLaunched) &&
                <Button
                  variant='contained'
                  color='primary'
                  target='_blank'
                  className={classes.button}
                  onClick={launch}
                >
                  {t('processDetail:startFlow')}
                </Button>
              }
              {hasPermission && (type === 'document' && item.status === CONSTANTS.DOCUMENT.STATUS.INIT && !item.bundleId) &&
                <Button
                  variant='contained'
                  color='primary'
                  className={classes.button}
                  component={RouterLink}
                  to={`/document/flow/${item.id}`}
                >
                  {t('processDetail:createFlow')}
                </Button>
              }

              <Button
                variant='outlined'
                className={classes.button}
                aria-label='delete'
                onClick={() => history.push(type === 'bundle' ? '/bundle' : '/document')}
              >
                {t('processDetail:back')}
              </Button>
            </div>
          </div>
        </Grid>
      </Grid>

      {hasPermission && (item.status === CONSTANTS.DOCUMENT.STATUS.INIT && !item.bundleId) &&
        <Grid item container xs={12} justify='center'>
          <Grid item xs={10}>
            <SignaturePositionsInfoAlert />
          </Grid>
        </Grid>
      }
      {item &&
        <Grid item container xs={12} justify='center'>
          <Grid item xs={10} >
            <Viewer match={match} type={type} document={type === 'bundle' ? item.document.items : item} bundleDocument={item} />
          </Grid>
        </Grid>
      }
    </Grid>
  );
};

DesktopView.propTypes = {
  type: PropTypes.string.isRequired,
  item: PropTypes.object.isRequired,
  launch: PropTypes.func,
  match: PropTypes.object,
  appConfig: PropTypes.object,
  isLaunched: PropTypes.bool
};

const ProcessDetail = (props) => {
  const { match, launch, getItemData, type, bundles, documents, appConfig, context } = props;
  const windowSize = useWindowSize();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState(false);
  const [errorLaunch, setErrorLaunch] = useState(null);
  const [item, setItem] = useState({});
  const [isLaunched, setIsLaunched] = useState(false);

  useEffect(() => {
    async function fetchBundle () {
      await getItemData(setMessage, setItem, setError, setLoading, match.params.id, appConfig);
    }

    fetchBundle();
  }, [bundles, documents]);

  const optimisticLaunch = async () => {
    setIsLaunched(true); // Optimistically update UI
    try {
      await launch(setMessage, setLoading, setItem, item, context, setErrorLaunch);
    } catch (error) {
      setIsLaunched(false); // Revert optimistic update if there's an error
      setErrorLaunch(error);
    }
  };

  if (loading) return <LoadingCard open={loading} loading message={message} />;

  if (!item || !Object.keys(item).length) {
    const typeNF = props.type || 'resource';
    return <PageContentError type={Types.NOT_FOUND} text={t(`error:notFound:${typeNF}NotFound`)} />;
  }

  return (
    <>
      {errorLaunch &&
        <AlertDialog
          open={!!errorLaunch}
          title={errorLaunch.message}
          description={errorLaunch.response.data.errors.join('\n')}
          setOpen={() => setErrorLaunch(null)}
          disableCancel />}
      {!error &&
        (windowSize.width > 500
          ? <DesktopView
            type={type}
            item={item}
            launch={optimisticLaunch}
            match={match}
            appConfig={appConfig}
            isLaunched={isLaunched}
          />
          : <MobileView
            type={type}
            item={item}
            launch={optimisticLaunch}
            match={match}
            appConfig={appConfig}
            context={context}
          />)
      }
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    bundles: state.bundles.bundles,
    appConfig: state.config,
    documents: state.documents.documents,
    context: state.config.context
  };
};

ProcessDetail.propTypes = {
  match: PropTypes.object.isRequired,
  launch: PropTypes.func,
  getItemData: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  bundles: PropTypes.array.isRequired,
  documents: PropTypes.array.isRequired,
  appConfig: PropTypes.object.isRequired

};

export default connect(mapStateToProps)(ProcessDetail);
